import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import Icon from '../components/Icon';
import CreditBar from '../components/CreditBar';
import { ProBadge } from '../components/ProSheet';
import { trialDaysLeft } from '../components/Trial';
import { AI_ACTIONS, USAGE_GROUPS, adPerRequest, watchAdForCredit } from '../lib/credits';
import { LOCALE } from '../lib/format';
import { relativeDayLabel, shortDate, toISODate } from '../lib/dates';
import { useApp, useAppPath } from '../lib/AppContext';

const CARD = 'rounded-[20px] bg-base-100 p-5';
const PLAN_NAMES = { free: 'Miễn phí', trial: 'Pro (dùng thử)', pro: 'Pro', gift: 'Pro Coach tặng', unlimited: 'Không giới hạn' };
const dayOf = (iso) => shortDate(toISODate(new Date(iso)));

// "Làm mới sau 1 giờ 15 phút"
const untilRefresh = (iso) => {
  const minutes = Math.max(0, Math.round((new Date(iso) - Date.now()) / 60000));
  const h = Math.floor(minutes / 60);
  return h > 0 ? `${h} giờ ${minutes % 60} phút` : `${minutes} phút`;
};

function PlanCard({ m }) {
  const { openPaywall } = useApp();
  const subline = m.tier === 'unlimited' ? 'Mọi tính năng, không giới hạn lượt AI'
    : m.tier === 'free' ? (adPerRequest(m) ? `${m.ads.daily} lượt AI mỗi ngày, mỗi lượt xem 1 quảng cáo` : `${m.credits.daily} lượt AI mỗi ngày`)
      : m.tier === 'trial' ? `Dùng thử · còn ${trialDaysLeft(m)} ngày, kết thúc ${dayOf(m.trial_ends_at)} · không tự gia hạn`
        : m.tier === 'gift' ? `Coach ${m.gift.coach_name} tặng${m.gift.ends_at ? ` đến hết ngày ${dayOf(m.gift.ends_at)}` : ''}`
          : `Gia hạn ngày ${dayOf(m.pro_until)}`;

  return (
    <section data-theme="macro-dark" className="flex flex-col gap-4 rounded-[20px] bg-base-200 p-5 text-base-content">
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-wide text-muted">Gói hiện tại</div>
          <div className="flex items-center gap-2 text-2xl font-extrabold">{m.pro && <ProBadge />}{PLAN_NAMES[m.tier]}</div>
          <div className="text-sm text-muted">{subline}</div>
        </div>
        <span className="rounded-full bg-base-300 px-3 py-1 text-xs font-semibold">Đang dùng</span>
      </div>
      {(m.tier === 'free' || m.tier === 'trial') && (
        <>
          <div className="rounded-[14px] bg-base-100 p-4">
            <div className="font-bold">Pro: 20 lượt AI mỗi ngày, 200 lượt mỗi tháng</div>
            <p className="text-sm text-muted">Chụp món, gợi ý thực đơn và gợi ý mục tiêu thoải mái hơn, không quảng cáo. Từ 45.750 ₫/tháng (gói năm).</p>
          </div>
          <button type="button" className="btn h-12 rounded-[14px] border-0 bg-[#6fd3a0] text-base font-bold text-[#15261f]"
            onClick={() => openPaywall(m.trial_available ? 'trial_offer' : 'upgrade')}>
            {m.trial_available ? 'Dùng thử Pro 7 ngày' : m.tier === 'trial' ? 'Đăng ký Pro' : 'Nâng cấp Pro'}
          </button>
        </>
      )}
    </section>
  );
}

function TodayCard({ m }) {
  const { api, native, setMembership } = useApp();
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState(null);
  const unlimited = m.credits.left == null;
  const perRequest = adPerRequest(m);
  const canWatch = perRequest && m.ads.left > 0 && native?.showRewardedAd;
  const watch = async () => {
    setBusy(true);
    setNote(null);
    try {
      await watchAdForCredit({ api, native, setMembership, membership: m });
    } catch (e) {
      setNote(e.message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <section className={`${CARD} flex flex-col gap-3`}>
      <div className="flex items-baseline justify-between gap-2">
        <h2 className="text-lg font-bold">Hôm nay</h2>
        {!unlimited && <span className="text-xs text-muted">Làm mới sau {untilRefresh(m.credits.refresh_at)}</span>}
      </div>
      {unlimited ? (
        <p className="text-sm"><b className="text-3xl font-extrabold">∞</b> <span className="text-muted">không giới hạn · đã dùng {m.credits.used} lượt</span></p>
      ) : perRequest ? (
        <>
          <p><b className="tabular text-4xl font-extrabold text-brand-text">{m.ads.left + m.credits.left}</b> <span className="text-muted">/ {m.ads.daily} lượt AI còn lại</span></p>
          <CreditBar left={m.ads.left + m.credits.left} limit={m.ads.daily} />
          <p className="text-sm">Mỗi lượt AI cần xem một quảng cáo ngắn{m.credits.left > 0 ? `. Bạn đang có ${m.credits.left} lượt đã xem quảng cáo, chưa dùng` : ''}.{native?.showRewardedAd ? '' : ' Quảng cáo chỉ có trong app trên điện thoại.'}</p>
        </>
      ) : (
        <>
          <p><b className="tabular text-4xl font-extrabold text-brand-text">{m.credits.left}</b> <span className="text-muted">/ {m.credits.limit} lượt còn lại</span></p>
          <CreditBar left={m.credits.left} limit={m.credits.limit} />
          {m.month && (
            <p className="tabular text-sm">Tháng này: <b>{m.month.used}/{m.month.limit}</b> lượt đã dùng · làm mới ngày {m.month.resets_on.slice(8, 10).replace(/^0/, '')}/{Number(m.month.resets_on.slice(5, 7))}</p>
          )}
          {m.menus?.limit != null && m.pro && (
            <p className="tabular text-sm">Thực đơn tuần này: <b>{m.menus.used}/{m.menus.limit}</b> lần tạo</p>
          )}
        </>
      )}
      <p className="text-xs text-muted">Lượt chỉ bị trừ khi AI trả kết quả thành công. Lỗi mạng hoặc ảnh không nhận diện được sẽ không tính lượt.</p>
      {canWatch && (
        <button type="button" className="flex min-h-14 items-center gap-3 rounded-[16px] border border-line px-4 text-left"
          disabled={busy} onClick={watch}>
          <span className="grid size-9 place-items-center rounded-full bg-[#e3f1e9] text-[#17804f]">
            {busy ? <span className="loading loading-spinner loading-xs" /> : <Icon name="chevronRight" className="size-5" />}
          </span>
          <span className="flex-1">
            <span className="block font-semibold">Xem quảng cáo, nhận 1 lượt</span>
            <span className="block text-xs text-muted">Hôm nay còn {m.ads.left} lần xem</span>
          </span>
          <Icon name="chevronRight" className="size-5 text-muted" />
        </button>
      )}
      {note && <p role="status" className="text-sm text-muted">{note}</p>}
    </section>
  );
}

const PREVIEW = 8;
const RANGES = [['today', 'Hôm nay'], ['7d', '7 ngày']];

// "Lượt đã dùng": for today or the last 7 days, what the credits went to (one bar per feature), then each use
// under it (with failures at 0 and ad rewards at +1), newest first, so the summary and its details sit together.
function UsageHistoryCard() {
  const { api } = useApp();
  const [range, setRange] = useState('7d');
  const [usage, setUsage] = useState(null);
  const [entries, setEntries] = useState(null);
  const [more, setMore] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const load = (before) => api.get(`/membership/history?${new URLSearchParams({ range, ...(before ? { before } : {}) })}`).then((d) => {
    setEntries((prev) => (before ? [...prev, ...d.entries] : d.entries));
    setMore(d.more);
  }).catch(() => setEntries((prev) => prev || []));
  useEffect(() => {
    setUsage(null);
    setEntries(null);
    setExpanded(false);
    api.get(`/membership/usage?range=${range}`).then((d) => setUsage(d.by_action)).catch(() => setUsage({}));
    load();
  }, [api, range]); // eslint-disable-line react-hooks/exhaustive-deps

  const rows = USAGE_GROUPS.map((g) => ({ ...g, count: g.actions.reduce((sum, a) => sum + (usage?.[a] || 0), 0) }));
  const total = rows.reduce((sum, r) => sum + r.count, 0);
  const max = Math.max(1, ...rows.map((r) => r.count));
  const period = range === '7d' ? 'trong 7 ngày qua' : 'hôm nay';

  const shown = expanded ? entries : entries?.slice(0, PREVIEW);
  const groups = [];
  shown?.forEach((e) => {
    const day = toISODate(new Date(e.created_at));
    if (groups.at(-1)?.day !== day) groups.push({ day, rows: [] });
    groups.at(-1).rows.push(e);
  });

  return (
    <section className={`${CARD} flex flex-col gap-3`}>
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-lg font-bold">Lượt đã dùng</h2>
        <div role="tablist" className="grid grid-cols-2 gap-1 rounded-[12px] bg-base-200 p-1 text-xs font-semibold">
          {RANGES.map(([id, label]) => (
            <button key={id} type="button" role="tab" aria-selected={range === id} onClick={() => setRange(id)}
              className={`h-9 rounded-[9px] px-3 ${range === id ? 'bg-base-100 shadow-sm' : 'text-muted'}`}>{label}</button>
          ))}
        </div>
      </div>
      {!usage ? <div className="skeleton h-32 rounded-[14px]" /> : (
        <>
          {rows.map((r) => (
            <div key={r.label} className="flex flex-col gap-1">
              <div className="flex justify-between text-sm"><span>{r.label}</span><b className="tabular">{r.count} lượt</b></div>
              <div className="h-1.5 rounded-full bg-base-200">
                <div className="h-full rounded-full" style={{ width: `${(r.count / max) * 100}%`, background: r.color }} />
              </div>
            </div>
          ))}
          <p className="text-xs text-muted">Tổng {total} lượt {period}</p>
        </>
      )}

      <div className="border-t border-line pt-1">
        {!entries && <div className="skeleton mt-2 h-24 rounded-[14px]" />}
        {entries?.length === 0 && <p className="pt-2 text-sm text-muted">Chưa dùng lượt AI nào {period}.</p>}
        {groups.map((g) => (
          <div key={g.day}>
            <div className="pb-1 pt-2 text-[11px] font-bold uppercase tracking-wide text-muted">{relativeDayLabel(g.day)}</div>
            <ul className="flex flex-col">
              {g.rows.map((e) => {
                const meta = AI_ACTIONS[e.action] || { label: e.action, color: '#888' };
                const failed = e.status === 'failed';
                const bonus = e.action === 'ad_reward' || e.action === 'admin_grant';
                const title = bonus ? meta.label : `${meta.label}${e.label ? ` · ${e.label}` : ''}`;
                const time = new Date(e.created_at).toLocaleTimeString(LOCALE, { hour: '2-digit', minute: '2-digit' });
                return (
                  <li key={e.id} className="flex items-center gap-3 py-2">
                    <span className="size-2 shrink-0 rounded-full" style={{ background: failed ? '#c5ccc8' : meta.color }} />
                    <span className="min-w-0 flex-1">
                      <span className={`block truncate text-sm font-semibold ${failed ? 'text-muted' : ''}`}>{failed ? `${meta.label} · Lỗi` : title}</span>
                      <span className="block text-xs text-muted">
                        {time}{failed ? ' · không trừ lượt' : e.status === 'rejected' ? ' · nội dung không liên quan' : e.status === 'free' ? ' · miễn phí' : bonus ? ' · nhận thêm lượt' : ''}
                      </span>
                    </span>
                    <span className={`tabular shrink-0 text-sm font-bold ${e.credits > 0 ? 'text-brand-text' : e.credits === 0 ? 'text-muted' : ''}`}>
                      {e.credits > 0 ? `+${e.credits}` : e.credits === 0 ? '0' : `−${Math.abs(e.credits)}`}
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
        {entries && (entries.length > PREVIEW || more) && (!expanded || more) && (
          <button type="button" className="min-h-11 w-full text-sm font-semibold text-brand-text"
            onClick={() => (!expanded ? setExpanded(true) : load(entries.at(-1).id))}>
            {!expanded ? 'Xem tất cả' : 'Xem thêm'}
          </button>
        )}
      </div>
    </section>
  );
}

// "Gói & lượt AI": the plan, today's credits, what they went to with each use, and payment management.
export default function CreditsPage() {
  const { api, membership, setMembership } = useApp();
  const navigate = useNavigate();
  const location = useLocation();
  const path = useAppPath();
  const [note, setNote] = useState(null);

  // Fresh numbers on every visit (the header-carried credits may be from earlier).
  useEffect(() => { api.get('/membership').then(setMembership).catch(() => {}); }, [api, setMembership]);
  const back = () => (location.key !== 'default' ? navigate(-1) : navigate(path('/settings')));

  return (
    <div className="flex flex-col gap-4">
      <div className="pt-safe flex items-center gap-1 pt-4">
        <button type="button" className="btn btn-circle btn-ghost -ml-2 size-11" aria-label="Quay lại" onClick={back}>
          <Icon name="chevronLeft" className="size-6" />
        </button>
        <h1 className="text-[22px] font-bold">Gói & lượt AI</h1>
      </div>
      {!membership ? <div className="skeleton h-64 rounded-[20px]" /> : (
        <>
          <PlanCard m={membership} />
          <TodayCard m={membership} />
          <UsageHistoryCard />
          <div className="divide-y divide-line rounded-[20px] bg-base-100 px-4">
            {/* Changing or cancelling happens in the store's own subscription page. */}
            <a href="https://apps.apple.com/account/subscriptions" target="_blank" rel="noreferrer"
              className="flex min-h-14 items-center justify-between gap-3 py-2">
              <span>
                <span className="block text-[15px] font-medium">Quản lý thanh toán</span>
                <span className="block text-xs text-muted">Đổi hoặc hủy gói qua App Store / Google Play</span>
              </span>
              <Icon name="chevronRight" className="size-5 text-muted" />
            </a>
            <button type="button" className="flex min-h-14 w-full items-center text-left text-[15px] font-medium"
              onClick={() => setNote('Khôi phục giao dịch sẽ có khi Macro Mate phát hành trên App Store.')}>
              Khôi phục giao dịch
            </button>
          </div>
          {note && <p role="status" className="text-center text-sm text-muted">{note}</p>}
        </>
      )}
    </div>
  );
}
